import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from './alert.service';
import { T } from 'src/assets/i18n/translation-keys';
import { Configuration } from 'src/app/app.constants';
import { FILE_VALIDATION_CONSTANTS, MIME_TYPE_MAPPINGS } from '../constants/file-validation.constants';

export interface FileValidationResult {
  validFiles: File[];
  invalidFiles: File[];
  totalFiles: number;
}

export interface FileValidationOptions {
  allowedTypes?: string[];
  showErrors?: boolean;
  showSummaryOnly?: boolean;
  maxFiles?: number;
}

export interface DragValidationResult {
  isValid: boolean;
  invalidReason?: 'size' | 'type' | 'unknown' | 'count';
  invalidFiles?: File[];
  validCount?: number;
  invalidCount?: number;
  hasMixed?: boolean;
  exceedsMaxFiles?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FileValidationService {

  constructor(
    private readonly alertService: AlertService,
    private readonly translateService: TranslateService,
    private readonly configuration: Configuration
  ) {}

  /**
   * Validates multiple files and returns validation result
   * @param files FileList or File array to validate
   * @param configuration System configuration with file limits
   * @param showErrors Whether to show error messages for invalid files
   * @returns FileValidationResult with valid and invalid files
   */
  validateFiles(
    files: FileList | File[],
    configuration: Configuration,
    showErrors: boolean = true
  ): FileValidationResult {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    for (const file of fileArray) {
      if (this.validateSingleFile(file, configuration, showErrors)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    }

    return {
      validFiles,
      invalidFiles,
      totalFiles: fileArray.length
    };
  }

  /**
   * Validates multiple files with custom options
   * @param files FileList or File array to validate
   * @param options Custom validation options
   * @returns FileValidationResult with valid and invalid files
   */
  validateFilesWithOptions(
    files: FileList | File[],
    options: FileValidationOptions
  ): FileValidationResult {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    const validationOptions = { ...options };

    if (options.showSummaryOnly && options.showErrors) {
      validationOptions.showErrors = false;
    }

    for (const file of fileArray) {
      if (this.validateSingleFileWithOptions(file, validationOptions)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    }

    if (options.showSummaryOnly && options.showErrors && invalidFiles.length > 0) {
      const message = invalidFiles.length === 1
        ? this.translateService.instant(T.common.the_file_is_not_supported_try_different_type)
        : `${invalidFiles.length} files were skipped (unsupported type or too large)`;
      void this.alertService.error(message);
    }

    return {
      validFiles,
      invalidFiles,
      totalFiles: fileArray.length
    };
  }

  /**
   * Validates files during drag operation (before drop) for visual feedback
   * @param files FileList from drag event or DataTransferItemList
   * @param options Custom validation options
   * @returns DragValidationResult with validation status and reason
   */
  validateDragFiles(
    files: FileList | File[] | DataTransferItemList,
    options: FileValidationOptions
  ): DragValidationResult {
    if (files && 'length' in files && files[0] && 'kind' in files[0]) {
      return this.validateDataTransferItems(files as DataTransferItemList, options);
    }

    const fileArray = Array.from(files as FileList | File[]);

    const exceedsMaxFiles = options.maxFiles && fileArray.length > options.maxFiles;
    if (exceedsMaxFiles) {
      return {
        isValid: false,
        invalidReason: 'count',
        invalidFiles: fileArray,
        validCount: 0,
        invalidCount: fileArray.length,
        hasMixed: false,
        exceedsMaxFiles: true
      };
    }

    const invalidFiles: File[] = [];
    let validCount = 0;
    let invalidCount = 0;
    let invalidReason: 'size' | 'type' | 'unknown' | undefined;

    for (const file of fileArray) {
      const validation = this.validateSingleFileWithOptionsDetailed(file, options);
      if (!validation.isValid) {
        invalidFiles.push(file);
        invalidCount++;
        if (!invalidReason) {
          invalidReason = validation.reason;
        }
      } else {
        validCount++;
      }
    }

    const hasMixed = validCount > 0 && invalidCount > 0;
    const isValid = validCount > 0;

    return {
      isValid,
      invalidReason: invalidCount > 0 ? invalidReason : undefined,
      invalidFiles,
      validCount,
      invalidCount,
      hasMixed,
      exceedsMaxFiles: false
    };
  }

  /**
   * Validates DataTransferItemList during drag operations
   * @param items DataTransferItemList from drag event
   * @param options Custom validation options
   * @returns DragValidationResult with validation status and reason
   */
  private validateDataTransferItems(
    items: DataTransferItemList,
    options: FileValidationOptions
  ): DragValidationResult {
    const fileCount = Array.from(items).filter(item => item.kind === 'file').length;
    const exceedsMaxFiles = options.maxFiles && fileCount > options.maxFiles;

    if (exceedsMaxFiles) {
      return {
        isValid: false,
        invalidReason: 'count',
        invalidFiles: [],
        validCount: 0,
        invalidCount: fileCount,
        hasMixed: false,
        exceedsMaxFiles: true
      };
    }

    let validCount = 0;
    let invalidCount = 0;

    let allowedTypes: string[];
    if (options.allowedTypes && options.allowedTypes.length > 0) {
      allowedTypes = this.normalizeAllowedTypes(options.allowedTypes);
    } else {
      const allowedTypesString = this.configuration.AllowedUploadTypes || FILE_VALIDATION_CONSTANTS.DEFAULT_ALLOWED_TYPES;
      allowedTypes = this.parseAllowedTypes(allowedTypesString);
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.kind === 'file') {
        const mimeType = item.type;
        const extension = this.getExtensionFromMimeType(mimeType);

        if (!extension || !allowedTypes.includes(extension)) {
          invalidCount++;
        } else {
          validCount++;
        }
      }
    }

    const hasMixed = validCount > 0 && invalidCount > 0;
    const isValid = validCount > 0;

    return {
      isValid,
      invalidReason: invalidCount > 0 ? 'type' : undefined,
      validCount,
      invalidCount,
      hasMixed,
      exceedsMaxFiles: false
    };
  }

  /**
   * Gets file extension from MIME type
   * @param mimeType MIME type string
   * @returns File extension or empty string
   */
  private getExtensionFromMimeType(mimeType: string): string {
    return MIME_TYPE_MAPPINGS[mimeType as keyof typeof MIME_TYPE_MAPPINGS] || '';
  }

  /**
   * Validates a single file against configuration rules
   * @param file File to validate
   * @param configuration System configuration
   * @param showError Whether to show error message if invalid
   * @returns true if file is valid, false otherwise
   */
  validateSingleFile(file: File, configuration: Configuration, showError: boolean = true): boolean {
    const maxFileSize = configuration?.MaxFileSize || FILE_VALIDATION_CONSTANTS.DEFAULT_MAX_FILE_SIZE;
    if (file.size > maxFileSize) {
      if (showError) {
        void this.alertService.error(
          this.translateService.instant(T.common.the_file_is_too_large_try_smaller)
        );
      }
      return false;
    }

    const extension = this.getFileExtension(file.name);

    const allowedTypesString = configuration.AllowedUploadTypes || FILE_VALIDATION_CONSTANTS.DEFAULT_ALLOWED_TYPES;
    const allowedTypes = this.parseAllowedTypes(allowedTypesString);

    if (!allowedTypes.includes(extension)) {
      if (showError) {
        void this.alertService.error(
          this.translateService.instant(T.common.the_file_is_not_supported_try_different_type) +
          `${this.translateService.instant(T.common.allowed_types)}: ${allowedTypes.join(', ')}`
        );
      }
      return false;
    }

    return true;
  }

  /**
   * Validates a single file with custom options
   * @param file File to validate
   * @param options Custom validation options
   * @returns true if file is valid, false otherwise
   */
  validateSingleFileWithOptions(file: File, options: FileValidationOptions): boolean {
    const validation = this.validateSingleFileWithOptionsDetailed(file, options);

    if (!validation.isValid && options.showErrors) {
      if (validation.reason === 'size') {
        void this.alertService.error(
          this.translateService.instant(T.common.the_file_is_too_large_try_smaller)
        );
      } else if (validation.reason === 'type') {
        const allowedTypes = this.normalizeAllowedTypes(options.allowedTypes || []);
        void this.alertService.error(
          this.translateService.instant(T.common.the_file_is_not_supported_try_different_type) +
          ` ${this.translateService.instant(T.common.allowed_types)}: ${allowedTypes.join(', ')}`
        );
      }
    }

    return validation.isValid;
  }

  /**
   * Validates a single file with detailed result
   * @param file File to validate
   * @param options Custom validation options
   * @returns Detailed validation result with reason
   */
  validateSingleFileWithOptionsDetailed(file: File, options: FileValidationOptions): { isValid: boolean; reason?: 'size' | 'type' | 'unknown' } {
    const maxFileSize = this.configuration?.MaxFileSize || FILE_VALIDATION_CONSTANTS.DEFAULT_MAX_FILE_SIZE;
    if (file.size > maxFileSize) {
      return { isValid: false, reason: 'size' };
    }

    const extension = this.getFileExtension(file.name);
    let allowedTypes: string[];

    if (options.allowedTypes && options.allowedTypes.length > 0) {
      allowedTypes = this.normalizeAllowedTypes(options.allowedTypes);
    } else {
      const allowedTypesString = this.configuration.AllowedUploadTypes || FILE_VALIDATION_CONSTANTS.DEFAULT_ALLOWED_TYPES;
      allowedTypes = this.parseAllowedTypes(allowedTypesString);
    }

    if (!allowedTypes.includes(extension)) {
      return { isValid: false, reason: 'type' };
    }

    return { isValid: true };
  }

  /**
   * Parses allowed types string from backend configuration
   * Handles both comma and space separated values, with or without dots
   * @param allowedTypesString String from backend like ".jpg, .jpeg, .png"
   * @returns Array of normalized file extensions
   */
  private parseAllowedTypes(allowedTypesString: string): string[] {
    if (!allowedTypesString) return [];

    return allowedTypesString
      .split(/[,\s]+/)
      .map(type => type.trim().toLowerCase().replace(/^\./, ''))
      .filter(type => type.length > 0);
  }

  /**
   * Normalizes array of allowed types (removes dots, converts to lowercase)
   * @param allowedTypes Array of file types
   * @returns Normalized array of file extensions
   */
  private normalizeAllowedTypes(allowedTypes: string[]): string[] {
    return allowedTypes.map(type =>
      type.trim().toLowerCase().replace(/^\./, '')
    );
  }

  /**
   * Extracts file extension from filename
   * @param filename Name of the file
   * @returns Lowercase file extension
   */
  private getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  /**
   * Formats file size for display
   * @param bytes File size in bytes
   * @returns Human readable file size string
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Gets maximum file size display string from configuration
   * @param configuration System configuration
   * @returns Formatted max file size string
   */
  getMaxFileSizeDisplay(configuration: Configuration): string {
    if (configuration.MaxFileSize === 0) {
      return '4MB';
    }
    return this.formatFileSize(configuration.MaxFileSize);
  }
}
